import React from 'react';
import { UserProfile, Client } from '@/shared/types';

interface MainContentProps {
  profile: UserProfile;
  client?: Client | null;
  metaData: { label: string; value: string }[];
  footerNote?: string;
  config: any;
  LogoImage: React.FC<{ size: 'sm' | 'md' | 'lg' | 'xl'; className?: string; light?: boolean }>;
  type: string;
  children: React.ReactNode;
}

export const MainContent: React.FC<MainContentProps> = ({
  profile, client, metaData, footerNote, config, LogoImage, type, children
}) => {
  const densityClass = config.density === 'compact' ? 'space-y-1' : config.density === 'spacious' ? 'space-y-4' : 'space-y-2';

  return (
    <div className={`flex-1 flex flex-col ${['sidebar-left', 'dark-sidebar'].includes(config.header) ? 'p-0' : 'p-12'}`}>

      {/* Top Meta for non-header layouts */}
      {['gradient-top', 'banner-top', 'soft-gradient'].includes(config.header) && (
        <div className="flex justify-between items-start mb-12">
          <div className="flex gap-4 items-center">
            <LogoImage size="md" />
            <div>
              <h1 className="text-2xl font-bold">{profile.name}</h1>
              <p className="text-sm text-gray-500">{profile.address}</p>
            </div>
          </div>
          <div className="text-right">
            <h2 className="text-4xl font-light text-gray-300 uppercase">{type}</h2>
            <div className="flex flex-col items-end text-sm mt-2">
              {metaData.map(m => (
                <div key={m.label} className="flex gap-4">
                  <span className="font-bold text-gray-400 uppercase text-xs pt-0.5">{m.label}</span>
                  <span className="font-bold text-gray-800">{m.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Client Block */}
      <div className={`grid grid-cols-2 gap-12 mb-12 ${config.header.includes('sidebar') ? 'px-12 pt-12' : ''}`}>
        <div>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 border-b border-gray-100 pb-2">Issued To</p>
          {client ? (
            <div className={densityClass}>
              <p className="text-xl font-bold text-gray-900">{client.name}</p>
              <p className="text-sm text-gray-600 font-medium">{client.company}</p>
              <p className="text-sm text-gray-500">{client.address}</p>
              <p className="text-sm text-gray-500">{client.email}</p>
            </div>
          ) : <span className="text-gray-300 italic">No Client</span>}
        </div>

        {/* Meta for layouts where it wasn't in header */}
        {!['split-hero', 'split-hero-navy', 'dark-header', 'hero-block', 'gradient-top', 'banner-top', 'soft-gradient'].includes(config.header) && (
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 border-b border-gray-100 pb-2">Document Details</p>
            <div className={`grid grid-cols-2 gap-4 ${densityClass}`}>
              {metaData.map(m => (
                <div key={m.label}>
                  <p className="text-xs text-gray-400 uppercase">{m.label}</p>
                  <p className="font-bold text-gray-800">{m.value}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Content Table */}
      <div className={`flex-1 ${config.header.includes('sidebar') ? 'px-12' : ''}`}>
        {/* Injecting custom class for table styling based on config.table */}
        <div className={`corporate-table-${config.table} w-full`}>
          {children}
        </div>
      </div>

      {/* Footer */}
      <div className={`mt-auto pt-8 border-t border-gray-100 ${config.header.includes('sidebar') ? 'mx-12 mb-12' : ''}`}>
        <div className="flex justify-between items-end">
          <div className="max-w-md">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Terms & Notes</p>
            <p className="text-sm text-gray-600 leading-relaxed italic">{footerNote}</p>
          </div>
          {!config.header.includes('sidebar') && <LogoImage size="sm" className="opacity-50 grayscale"/>}
        </div>
      </div>
    </div>
  );
};